"use client";

import {
  type AttendeeType,
  type SignupStep,
  useSignupFunnel,
} from "@/hooks/useSignupFunnel";
import type { SpondProfileSummary } from "@/hooks/useSpondProfile";
import type { EventSource } from "@/types/discover";
import { useEmbeddedInApp } from "@/contexts/EmbeddedInAppContext";
import { track } from "@/lib/analytics/client";
import type { SignupSurface } from "@/lib/analytics/events";
import {
  allowedAttendeeTypes,
  requiresAdultSelfRegistration,
} from "@/utils/eventAgeGroup";
import { isPublicEvent } from "@/utils/eventHost";
import { ArrowLeft } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import AdultDetailsStep from "./AdultDetailsStep";
import ChildDetailsStep from "./ChildDetailsStep";
import ConfirmEmailStep from "./ConfirmEmailStep";
import CreateAccountStep from "./CreateAccountStep";
import EmbeddedLoginGate from "./EmbeddedLoginGate";
import LoginStep from "./LoginStep";
import SelectAttendeeStep from "./SelectAttendeeStep";
import SuccessStep from "./SuccessStep";
import TwoFactorStep from "./TwoFactorStep";

interface SignupFunnelProps {
  event: EventSource;
  locale: string;
  /** Step to start on — e.g. "select-attendee" after email verification. */
  initialStep?: SignupStep;
  /** Closes the sheet from inside the funnel (e.g. the success step's Finish). */
  onClose?: () => void;
  /**
   * Called when a signup is confirmed, with the attendee's custom link
   * (`attendanceUrl`) from the signup response — `null` when the response
   * carried none. Lets the parent send the user to their attendance page when
   * they close the sheet, instead of leaving them on the "Join us" view.
   */
  onSignupComplete?: (attendanceUrl: string | null) => void;
}

/**
 * Multi-step body of the public-event signup bottom sheet. Owns step/back state
 * (via {@link useSignupFunnel}) and renders the current step. The Sheet shell
 * (open state, close, dim overlay) lives in the parent; closing it unmounts
 * this component, so reopening always starts fresh.
 *
 * The design is mobile-only; on desktop the content is centred in a max-w-md
 * column inside the full-width bottom sheet so fields don't stretch edge to edge.
 */
export default function SignupFunnel({
  event,
  locale,
  initialStep,
  onClose,
  onSignupComplete,
}: SignupFunnelProps) {
  const t = useTranslations("publicEventSignup");
  const funnel = useSignupFunnel({ initialStep });
  // Focus target for step transitions (see the focus-management effect below).
  const stepContainerRef = useRef<HTMLDivElement>(null);
  // The sheet mounts focused by Radix; only MOVE focus on later step changes.
  const isInitialStep = useRef(true);
  // Email the account was created with, shown on the confirm-email gate.
  const [signupEmail, setSignupEmail] = useState("");
  // Redacted phone the 2FA code went to, shown on the two-factor step.
  const [twoFactorPhone, setTwoFactorPhone] = useState<string | null>(null);
  const allowedAttendees = allowedAttendeeTypes(event.age_group);
  const onlyAttendee =
    allowedAttendees.length === 1 ? allowedAttendees[0] : null;
  const requiresAdultAge = requiresAdultSelfRegistration(event.age_group);

  // Attendee branch chosen on select-attendee; the details step (2.6/2.7)
  const [attendeeType, setAttendeeType] = useState<AttendeeType | null>(
    onlyAttendee,
  );
  // Fetched once by EmbeddedLoginGate to confirm the transferred session; the
  // details steps reuse it instead of fetching /api/profile a second time.
  const [embeddedProfile, setEmbeddedProfile] = useState<
    SpondProfileSummary | undefined
  >(undefined);

  // DIS-1117 canonical-event dimensions, fixed for the funnel session.
  const {
    embeddedInApp: isEmbedded,
    sessionTransfer,
    resolved: surfaceResolved,
  } = useEmbeddedInApp();
  const surface: SignupSurface = isEmbedded ? "in_app" : "web";
  const groupId = event.group_id ?? null;
  // is_new_user (the KR attribution key): `?signup=select-attendee` is the
  // verify route's redirect marker, so its presence means the account was
  // created during this signup. Read from the URL — the contract the verify
  // route owns — rather than inferred from `initialStep`, so a future entry
  // path that also mounts at select-attendee can't inflate the KR. Caveat: an
  // existing account re-verified via the email link (anti-enumeration re-send)
  // is indistinguishable from a new one.
  const isNewUser = useSearchParams().get("signup") === "select-attendee";

  // DIS-1117 canonical funnel events fire once per funnel session (mount), so
  // back-navigation through a step doesn't double-count the KR dashboards.
  const hasTrackedAuthStarted = useRef(false);
  const hasTrackedSignupStarted = useRef(false);
  useEffect(() => {
    // The surface is read from the document after hydration; firing before
    // that would report the web view as "web".
    if (!surfaceResolved || !isPublicEvent(event)) return;

    // With a session transfer, the app signs the user in: no form is shown.
    const showsSignInForm = funnel.step === "login" && !sessionTransfer;
    if (showsSignInForm && !hasTrackedAuthStarted.current) {
      hasTrackedAuthStarted.current = true;
      track("Public Event Auth Started", {
        event_id: event.id,
        group_id: groupId,
        signup_surface: surface,
      });
    }
    if (funnel.step === "select-attendee" && !hasTrackedSignupStarted.current) {
      hasTrackedSignupStarted.current = true;
      track("Public Event Signup Started", {
        event_id: event.id,
        group_id: groupId,
        signup_surface: surface,
        is_new_user: isNewUser,
      });
    }
  }, [
    funnel.step,
    event,
    groupId,
    surface,
    surfaceResolved,
    sessionTransfer,
    isNewUser,
  ]);

  // Move focus when the funnel changes step. Without this, the button the user
  // clicked unmounts and focus falls to <body>, so keyboard and screen-reader
  // users are stranded and never hear that a new step rendered.
  // biome-ignore lint/correctness/useExhaustiveDependencies: funnel.step is the intentional trigger — the effect moves focus WHEN the step changes; it reads the DOM, not the step value, so the dep only looks "unused".
  useEffect(() => {
    if (isInitialStep.current) {
      isInitialStep.current = false;
      return;
    }
    const container = stepContainerRef.current;
    if (!container) return;
    // Prefer the step's heading so a screen reader announces the new step's
    // title (e.g. "Select attendee, heading") rather than a silent generic
    // container. Setting tabIndex here — instead of on every step's <h2> —
    // makes the heading programmatically focusable without adding it to the tab
    // order. Fall back to the container if a step ever lacks a heading.
    const target = container.querySelector<HTMLElement>("h2") ?? container;
    target.tabIndex = -1;
    target.focus();
  }, [funnel.step]);

  const handleEmbeddedAuthenticated = useCallback(
    (profile?: SpondProfileSummary) => {
      setEmbeddedProfile(profile);
      funnel.goTo("select-attendee", { replace: true });
    },
    [funnel.goTo],
  );

  function handleSelectAttendee(selected: AttendeeType) {
    // Record the branch for the details step (2.6/2.7) to read, then advance.
    // goTo pushes onto the history stack, so back() retraces to select-attendee.
    setAttendeeType(selected);
    funnel.goTo("details");
  }

  function handleDetailsSubmitted(attendanceUrl?: string) {
    track("Public Event Signup Completed", {
      event_id: event.id,
      group_id: groupId,
      signup_surface: surface,
      is_new_user: isNewUser,
    });
    onSignupComplete?.(attendanceUrl ?? null);
    funnel.goTo("success");
  }

  // Defensive guard: the sheet should only open for public events, but a
  // stale/deep-linked id must never render a half-built funnel.
  if (!isPublicEvent(event)) {
    return (
      <p className="py-8 text-center text-muted-foreground">
        {t("notAvailable")}
      </p>
    );
  }

  function renderAttendeeStep() {
    // Unrecognised age_group fails closed (no allowed attendee types).
    if (allowedAttendees.length === 0) {
      return (
        <p className="py-8 text-center text-muted-foreground">
          {t("signupErrorAttendeeNotAllowed")}
        </p>
      );
    }

    const showsDetails =
      funnel.step === "details" ||
      (funnel.step === "select-attendee" && onlyAttendee !== null);

    if (showsDetails && attendeeType === "SELF") {
      return (
        <AdultDetailsStep
          eventId={event.id}
          profile={embeddedProfile}
          requireAdultAge={requiresAdultAge}
          onSubmitted={handleDetailsSubmitted}
        />
      );
    }

    if (showsDetails && attendeeType === "CHILD") {
      return (
        <ChildDetailsStep
          eventId={event.id}
          profile={embeddedProfile}
          onSubmitted={handleDetailsSubmitted}
        />
      );
    }

    return (
      <SelectAttendeeStep
        onSelect={handleSelectAttendee}
        allowed={allowedAttendees}
      />
    );
  }

  function renderStep() {
    switch (funnel.step) {
      case "login":
        // No sign-in form is ever reachable where the app transfers sessions.
        // A fresh transferred session may already be valid, so the gate
        // checks /api/profile first: a valid session skips past login.
        if (sessionTransfer) {
          return (
            <EmbeddedLoginGate onAuthenticated={handleEmbeddedAuthenticated} />
          );
        }
        return (
          <LoginStep
            eventId={event.id}
            locale={locale}
            onAuthenticated={() => funnel.goTo("select-attendee")}
            onTwoFactorRequired={(phoneNumber) => {
              setTwoFactorPhone(phoneNumber);
              funnel.goTo("two-factor");
            }}
            onCreateAccount={() => funnel.goTo("create-account")}
          />
        );
      case "two-factor":
        return (
          <TwoFactorStep
            eventId={event.id}
            phoneNumber={twoFactorPhone}
            // replace: the verify consumed the prompt, back() must not retrace into it.
            onAuthenticated={() =>
              funnel.goTo("select-attendee", { replace: true })
            }
          />
        );
      case "create-account":
        return (
          <CreateAccountStep
            eventId={event.id}
            locale={locale}
            onCreated={(email) => {
              // An already-registered email isn't a special case here: Spond Core
              // re-sends a verification code (anti-enumeration by design), so an
              // existing user proceeds through confirm-email like a new one.
              setSignupEmail(email);
              funnel.goTo("confirm-email");
            }}
          />
        );
      case "confirm-email":
        return <ConfirmEmailStep email={signupEmail} />;
      case "success":
        return <SuccessStep locale={locale} onFinish={() => onClose?.()} />;
      default:
        return renderAttendeeStep();
    }
  }

  return (
    <div className="mx-auto flex h-full w-full max-w-md flex-col gap-4">
      {funnel.canGoBack && (
        <button
          type="button"
          onClick={funnel.back}
          aria-label={t("back")}
          className="-ml-1 self-start rounded-full p-1 hover:bg-background-secondary"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
        </button>
      )}
      {/* Each step is a full-height column so its CTA can pin to the sheet
          bottom (design), with the body scrolling above it when tall.
          tabIndex={-1} lets the focus-management effect move focus here on a
          step change without making it a tab stop. */}
      <div
        ref={stepContainerRef}
        tabIndex={-1}
        className="flex flex-1 flex-col overflow-y-auto outline-none"
      >
        {renderStep()}
      </div>
    </div>
  );
}
